import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import CategoryIcon from "@/components/CategoryIcon";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { geocode } from "@/lib/geo";
import { HowItWorksDialog } from "@/components/HowItWorksDialog";
import { LaunchVideoDialog } from "@/components/LaunchVideoDialog";
import { SectionSuggestions } from "@/components/SectionSuggestions";
import { AdSlot } from "@/components/AdSlot";
import { Search, MapPin, ChevronDown, Star, HelpCircle, X, PlayCircle, ShieldCheck } from "lucide-react";

const RADII = [15, 30, 50, 100, "all"];
const ZONE_KEY = "fydate_zone_filter";
const INTRO_KEY = "fydate_intro_seen";
const BLOB = ["#FFD23F", "#8FB2FF", "#6FE0A0"];
const SPARK = ["#1F4FE0", "#FFB800", "#15924A"];
const TILE = "group relative bg-white rounded-[22px] shadow-[0_4px_18px_-6px_rgba(15,18,48,0.18)] hover:shadow-[0_10px_24px_-8px_rgba(31,79,224,0.45)] hover:-translate-y-1 active:scale-[0.96] transition-[transform,box-shadow] duration-150 app-pop flex flex-col items-center justify-end text-center px-3 pt-7 pb-4 min-h-[160px]";

function Sparkles({ i }) {
  return (
    <>
      <span className="absolute top-3 left-5 text-lg font-bold leading-none group-hover:rotate-90 transition-transform duration-150" style={{ color: SPARK[i % 3] }}>+</span>
      <span className="absolute bottom-[45%] right-[22%] text-xs font-bold leading-none group-hover:-rotate-90 transition-transform duration-150" style={{ color: SPARK[(i + 1) % 3] }}>×</span>
    </>
  );
}

function TileIcon({ icon, i }) {
  return (
    <span className="relative w-20 h-16 flex items-center justify-center mb-2">
      <span className="absolute w-12 h-12 rounded-full right-2 top-0 group-hover:scale-125 transition-transform duration-150" style={{ background: BLOB[i % 3] }} />
      <span className="relative text-[#0F1230]"><CategoryIcon name={icon} size={40} strokeWidth={2.1} /></span>
    </span>
  );
}

function NeedTile({ m, i, onClick }) {
  return (
    <button data-testid={`macro-tile-${m.id}`} onClick={onClick} className={TILE} style={{ animationDelay: `${i * 30}ms` }}>
      <Sparkles i={i} />
      <TileIcon icon={m.icon} i={i} />
      <h3 className="text-base font-extrabold text-[#0F1230] leading-tight">{m.label}</h3>
      {m.examples && <p data-testid={`macro-examples-${m.id}`} className="text-[11px] text-[#4A4E68] mt-0.5 leading-tight">{m.examples}</p>}
    </button>
  );
}

function HelpTile({ cat, i, n, label, interested, onClick }) {
  return (
    <button data-testid={`help-tile-${cat.slug}`} onClick={onClick}
      className={`${TILE} ${n === 0 ? "opacity-60" : ""} ${interested ? "ring-2 ring-[#1F4FE0]" : ""}`} style={{ animationDelay: `${i * 30}ms` }}>
      {interested && (
        <span data-testid={`interest-badge-${cat.slug}`} className="absolute -top-2 left-3 bg-[#1F4FE0] text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-0.5">
          <Star size={9} className="fill-white" /> Ti interessa
        </span>
      )}
      <Sparkles i={i} />
      <span data-testid={`count-${cat.slug}`} className={`absolute top-3 right-3 min-w-[28px] h-7 px-2 rounded-full text-sm font-extrabold flex items-center justify-center ${n > 0 ? "bg-[#FFC400] text-[#0F1230]" : "bg-[#EEF1F7] text-[#4A4E68]"}`}>{n}</span>
      <TileIcon icon={cat.icon} i={i} />
      <h3 className="text-sm font-extrabold text-[#0F1230] leading-tight">{label}</h3>
      <p className="text-xs font-semibold mt-0.5" style={{ color: n > 0 ? "#1F4FE0" : "#4A4E68" }}>{n > 0 ? `${n} richieste in zona` : "Nessuna richiesta"}</p>
    </button>
  );
}

function ZoneFilterBar({ zone, setZone }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const label = zone?.city ? `${zone.city}${zone.radius && zone.radius !== "all" ? ` · ${zone.radius} km` : " · Tutta Italia"}` : "Tutta Italia";

  const doSearch = async () => setResults(await geocode(query));
  const pick = (r) => { setZone({ ...zone, city: r.city, lat: r.lat, lng: r.lng, radius: zone?.radius || 30 }); setResults([]); setQuery(""); setOpen(false); };
  const setRadius = (r) => setZone({ ...zone, radius: r });

  return (
    <div data-testid="zone-filter" className="bg-white rounded-[22px] shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)] mb-4 overflow-hidden">
      <button data-testid="zone-filter-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 h-14 text-left">
        <MapPin size={20} className="text-[#1F4FE0]" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-[#0F1230]">La tua zona</span>
          <span className="block text-xs text-[#4A4E68] truncate">{label}</span>
        </span>
        <ChevronDown size={18} className={`text-[#0F1230] transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#EEF1F7] space-y-3">
          <form onSubmit={(e) => { e.preventDefault(); doSearch(); }} className="flex gap-2">
            <input data-testid="zone-filter-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Città o CAP (es. Padova)" className="flex-1 h-10 px-3 rounded-full bg-[#EEF1F7] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4FE0]/40" />
            <button type="submit" className="h-10 w-10 flex items-center justify-center rounded-full bg-[#1F4FE0] text-white shrink-0"><Search size={16} /></button>
          </form>
          {results.length > 0 && (
            <div className="border border-[#DADFEA] rounded-xl overflow-hidden">
              {results.map((r, i) => (
                <button key={i} data-testid={`zone-result-${i}`} onClick={() => pick(r)} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#EEF1F7] border-b border-[#DADFEA] last:border-0">{r.label}</button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {RADII.map((r) => (
              <button key={r} data-testid={`zone-radius-${r}`} onClick={() => setRadius(r)}
                className={`px-3 h-9 rounded-full text-sm font-semibold transition-colors ${zone?.radius === r ? "bg-[#1F4FE0] text-white" : "bg-[#EEF1F7] text-[#0F1230]"}`}>
                {r === "all" ? "Tutta Italia" : `${r} km`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function IntroCard({ onVideo, onHow, onClose }) {
  return (
    <div data-testid="intro-card" className="relative overflow-hidden bg-[#1F4FE0] text-white rounded-[22px] p-5 mb-5 app-pop">
      <span className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#4F7BFF]" />
      <span className="absolute right-12 bottom-5 text-2xl font-bold text-[#FFC400]">+</span>
      <button data-testid="intro-dismiss" onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white z-10"><X size={18} /></button>
      <h2 className="relative text-xl font-extrabold pr-6">Benvenuto su FYDATE</h2>
      <p className="relative text-sm text-white/85 mt-1 max-w-md">La <b className="text-white">bacheca dei bisogni</b> della tua zona: chi cerca un servizio incontra chi può offrirlo.</p>
      <div className="relative mt-3 space-y-1.5 text-sm text-white/80">
        <p><b className="text-[#FFC400]">Ho bisogno</b> — racconta di cosa hai bisogno e ricevi offerte da chi può aiutarti.</p>
        <p><b className="text-[#CFE0FF]">Posso aiutare</b> — guarda le richieste vicine e renditi disponibile.</p>
      </div>
      <div className="relative flex flex-wrap gap-2 mt-4">
        <button data-testid="intro-video" onClick={onVideo} className="bg-[#FFC400] text-[#0F1230] rounded-full px-4 h-10 text-sm font-bold inline-flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-transform duration-150"><PlayCircle size={16} /> Guarda il video</button>
        <button data-testid="intro-how" onClick={onHow} className="bg-white text-[#0F1230] rounded-full px-4 h-10 text-sm font-bold hover:scale-105 active:scale-95 transition-transform duration-150">Come funziona</button>
        <button data-testid="intro-ok" onClick={onClose} className="border border-white/30 text-white rounded-full px-4 h-10 text-sm font-bold hover:bg-white/10 transition-colors">Ho capito</button>
      </div>
    </div>
  );
}

export default function Home() {
  const { categories, catName, macros, macrosLoading } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("need");
  const [counts, setCounts] = useState({});
  const [dirQuery, setDirQuery] = useState("");
  const [zone, setZone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(ZONE_KEY)) || { radius: 30 }; } catch { return { radius: 30 }; }
  });

  useEffect(() => {
    if (!user) return;
    API.get("/me/full").then((r) => {
      const z = r.data?.zone;
      if (z && z.start_point && z.start_point.lat != null) {
        setZone({ city: z.start_point.city, lat: z.start_point.lat, lng: z.start_point.lng, radius: z.general_radius || 30 });
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) localStorage.setItem(ZONE_KEY, JSON.stringify(zone));
  }, [zone, user]);

  useEffect(() => {
    const params = {};
    if (zone && zone.lat != null && zone.radius && zone.radius !== "all") {
      params.lat = zone.lat; params.lng = zone.lng; params.radius = zone.radius;
    }
    API.get("/public/counts", { params }).then((r) => setCounts(r.data.counts || {})).catch(() => {});
  }, [zone]);

  const interests = user?.interests || [];
  const helpCats = React.useMemo(
    () => [...categories].sort((a, b) => (interests.includes(b.slug) ? 1 : 0) - (interests.includes(a.slug) ? 1 : 0)),
    [categories, user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [howOpen, setHowOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(() => {
    try { return !localStorage.getItem(INTRO_KEY); } catch { return true; }
  });
  const dismissIntro = () => {
    try { localStorage.setItem(INTRO_KEY, "1"); } catch {}
    setShowIntro(false);
  };

  return (
    <Layout>
      <HowItWorksDialog open={howOpen} onOpenChange={setHowOpen} />
      <LaunchVideoDialog open={videoOpen} onOpenChange={setVideoOpen} />
      <div className="fade-in">
        {showIntro && <IntroCard onVideo={() => setVideoOpen(true)} onHow={() => setHowOpen(true)} onClose={dismissIntro} />}

        <div data-testid="free-badge" className="h-11 rounded-2xl border-2 border-[#15924A] bg-[#DDF5E6] text-[#15924A] flex items-center gap-2 px-4 font-bold text-sm mb-4">
          <ShieldCheck size={18} /> Contatto diretto, senza intermediari
        </div>

        <h1 data-testid="mode-title" className="text-2xl sm:text-3xl font-extrabold text-[#0F1230] leading-tight mb-3">
          {mode === "need" ? "Di cosa hai bisogno?" : "Chi puoi aiutare oggi?"}
        </h1>

        <div className="flex items-center gap-2 mb-3">
          <div data-testid="mode-toggle" className="bg-white p-1 rounded-full flex items-center flex-1 max-w-md shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)]">
            <button data-testid="toggle-mode-ho-bisogno" onClick={() => setMode("need")} className={`flex-1 h-11 rounded-full text-sm font-bold transition-colors duration-150 ${mode === "need" ? "bg-[#FFC400] text-[#0F1230]" : "text-[#0F1230] hover:text-[#1F4FE0]"}`}>Ho bisogno</button>
            <button data-testid="toggle-mode-posso-aiutare" onClick={() => setMode("help")} className={`flex-1 h-11 rounded-full text-sm font-bold transition-colors duration-150 ${mode === "help" ? "bg-[#FFC400] text-[#0F1230]" : "text-[#0F1230] hover:text-[#1F4FE0]"}`}>Posso aiutare</button>
          </div>
          <button data-testid="home-video-button" onClick={() => setVideoOpen(true)} title="Guarda il video" className="w-11 h-11 shrink-0 rounded-full bg-[#1F4FE0] text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-150">
            <PlayCircle size={20} />
          </button>
          <button data-testid="how-it-works-button" onClick={() => setHowOpen(true)} title="Come funziona" className="w-11 h-11 shrink-0 rounded-full bg-white text-[#0F1230] flex items-center justify-center shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)] hover:scale-110 active:scale-90 transition-transform duration-150">
            <HelpCircle size={20} />
          </button>
        </div>

        <p data-testid="mode-helper" className="text-sm text-[#4A4E68] mb-4">
          {mode === "need"
            ? "Scegli un'area e racconta di cosa hai bisogno: ricevi offerte da chi può aiutarti."
            : "Guarda le richieste vicino a te e renditi disponibile per dare una mano."}
        </p>

        <form onSubmit={(e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(dirQuery)}`); }} className="relative mb-3">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F1230]" />
          <input data-testid="home-directory-search" value={dirQuery} onChange={(e) => setDirQuery(e.target.value)} placeholder="Cosa cerchi? (es. taxi, gommista, idraulico)" className="w-full h-12 pl-11 pr-4 rounded-full bg-white shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)] text-[#0F1230] font-medium placeholder:text-[#4A4E68] focus:outline-none focus:ring-2 focus:ring-[#1F4FE0]/50" />
        </form>
        {dirQuery.trim().length >= 2 && <SectionSuggestions query={dirQuery} mode={mode} />}

        {mode === "help" && (
          <>
            <ZoneFilterBar zone={zone} setZone={setZone} />
            {!user && (
              <div data-testid="preview-banner" className="bg-white rounded-[22px] p-4 mb-4 flex items-center gap-3 shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)]">
                <p className="text-sm text-[#0F1230]">{"Stai vedendo un'anteprima. Per renderti disponibile serve un account."}</p>
                <button data-testid="preview-signup" onClick={() => navigate("/register")} className="ml-auto bg-[#1F4FE0] text-white rounded-full px-4 h-10 text-sm font-bold shrink-0 hover:scale-105 transition-transform duration-150">Iscriviti</button>
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {mode === "need"
            ? (macrosLoading
                ? Array.from({ length: 9 }).map((_, i) => <div key={i} data-testid="macro-skeleton" className="bg-white rounded-[22px] min-h-[160px] animate-pulse" />)
                : macros.map((m, i) => <NeedTile key={m.id} m={m} i={i} onClick={() => navigate(`/need/${m.id}`)} />))
            : helpCats.map((c, i) => <HelpTile key={c.slug} cat={c} i={i} n={counts[c.slug] || 0} interested={interests.includes(c.slug)} label={catName(c)} onClick={() => navigate(`/category/${c.slug}`)} />)}
        </div>
        <AdSlot placement="home" className="mt-10" />
      </div>
    </Layout>
  );
}
